import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, useBlocker } from 'react-router-dom'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { usePageBlocks } from '@/hooks/usePages'
import { useEditor } from '@/hooks/useEditor'
import { useEditorStore } from '@/store/editorStore'
import { EditorToolbar } from '@/components/editor/EditorToolbar'
import { BlockPalette } from '@/components/editor/BlockPalette'
import { Canvas, EMPTY_CANVAS_DROP_ID } from '@/components/editor/Canvas'
import { PropertiesPanel } from '@/components/editor/PropertiesPanel'
import type { BlockType } from '@brix/types'
import type { Block } from '@/domain/model/Block'

export function EditorPage() {
  const { pageId = '' } = useParams()
  const navigate = useNavigate()
  const { data: blocks, isLoading } = usePageBlocks(pageId)
  const { loadPage, reset, previewMode, isDirty, save, undo, redo } = useEditor()
  const addBlock = useEditorStore(s => s.addBlock)
  const reorderBlocks = useEditorStore(s => s.reorderBlocks)
  const deleteBlock = useEditorStore(s => s.deleteBlock)
  const selectedBlockId = useEditorStore(s => s.selectedBlockId)
  const editorBlocks = useEditorStore(s => s.blocks)
  const clipboardRef = useRef<Block | null>(null)

  const [activeDragType, setActiveDragType] = useState<BlockType | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )

  useEffect(() => {
    if (blocks) loadPage(pageId, blocks)
    return () => reset()
  }, [blocks, pageId, loadPage, reset])

  // Keyboard shortcuts:
  //   Cmd/Ctrl+S save · Cmd/Ctrl+Z undo · Cmd/Ctrl+Shift+Z or Y redo
  //   Delete removes the selected block · Cmd/Ctrl+D duplicates it
  //   Cmd/Ctrl+C / Cmd/Ctrl+V copy/paste a block
  // Bare-key and Ctrl+C/V shortcuts are guarded so they never fire while the user
  // is typing in a sidebar field or in inline-editable canvas text (contentEditable).
  useEffect(() => {
    const isEditableTarget = (el: EventTarget | null) => {
      const target = el as HTMLElement | null
      return !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) {
        if (e.key === 's') {
          e.preventDefault()
          void save()
          return
        }
        if (e.key === 'z' && !e.shiftKey) {
          e.preventDefault()
          undo()
          return
        }
        if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
          e.preventDefault()
          redo()
          return
        }
        if (e.key === 'd') {
          const block = editorBlocks.find(b => b.id === selectedBlockId)
          if (block) {
            e.preventDefault()
            addBlock(block.type, block.props, block.id)
          }
          return
        }
        if (e.key === 'c' && !isEditableTarget(e.target)) {
          const block = editorBlocks.find(b => b.id === selectedBlockId)
          if (block) {
            e.preventDefault()
            clipboardRef.current = block
          }
          return
        }
        if (e.key === 'v' && !isEditableTarget(e.target)) {
          if (clipboardRef.current) {
            e.preventDefault()
            addBlock(clipboardRef.current.type, clipboardRef.current.props, selectedBlockId ?? undefined)
          }
          return
        }
        return
      }

      if (e.key === 'Delete' && !isEditableTarget(e.target) && selectedBlockId) {
        e.preventDefault()
        deleteBlock(selectedBlockId)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [save, undo, redo, addBlock, deleteBlock, editorBlocks, selectedBlockId])

  // Warn on tab/window close with unsaved changes
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => { if (isDirty) e.preventDefault() }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [isDirty])

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname,
  )

  const handleBack = () => navigate(-1)

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current
    if (data?.['type'] === 'palette') setActiveDragType(data['blockType'] as BlockType)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragType(null)
    const { active, over } = event

    const activeData = active.data.current
    if (activeData?.['type'] === 'palette') {
      const blockType = activeData['blockType'] as BlockType
      if (!over || over.id === EMPTY_CANVAS_DROP_ID) {
        addBlock(blockType)
        return
      }
      addBlock(blockType, undefined, over.id as string)
      return
    }

    if (!over || active.id === over.id) return
    reorderBlocks(active.id as string, over.id as string)
  }

  const handleDragCancel = () => setActiveDragType(null)

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-violet-600 border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-gray-100">
      <EditorToolbar pageId={pageId} onBack={handleBack} />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="flex flex-1 overflow-hidden">
          {!previewMode && <BlockPalette />}
          <Canvas />
          {!previewMode && <PropertiesPanel />}
        </div>

        <DragOverlay>
          {activeDragType && (
            <div className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white shadow-xl">
              + {activeDragType}
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {blocker.state === 'blocked' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="w-80 rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-base font-semibold text-gray-900">Unsaved changes</h3>
            <p className="mt-1 text-sm text-gray-500">Your changes will be lost if you leave. Save before leaving?</p>
            <div className="mt-5 flex gap-3">
              <button
                onClick={() => blocker.proceed?.()}
                className="flex-1 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Leave
              </button>
              <button
                onClick={() => blocker.reset?.()}
                className="flex-1 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700"
              >
                Stay
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
